import { Table, TableBody, TableCell, TableHead, TableRow } from "@material-ui/core";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import DragHandleIcon from "@material-ui/icons/DragHandle";
import SaveIcon from "@material-ui/icons/Save";
import arrayMove from "array-move";
import { useEffect, useState } from "react";
import { useNotify, BooleanField, Button, Filter, ListContextProvider, ListToolbar, Pagination, RecordContextProvider, TextField, TextInput, TopToolbar, useDataProvider, useList, useListContext, useListParams, useRedirect } from "react-admin";
import { SortableContainer, SortableElement, SortableHandle } from "react-sortable-hoc";
import { ButtonWithConfirm } from "./component/button-with-confirm";

const EpisodeFilter = ({ setProgrammeId, ...props }) => {
  return (
    <Filter {...props}>
      <TextInput
        label="Programme ID"
        source="programme_id"
        onChange={(e) => {
          const programmeId = e.target.value;
          setProgrammeId(programmeId);
        }}
        alwaysOn
      />
    </Filter>
  );
};

const updateEpisodeOrder = async (dataProvider, isReset, allItems) => {
  const order = allItems.map((item) => item.episode_id).reverse();
  await dataProvider.updateEpisodeOrder(isReset, order);
};

const ListActions = ({ programmeId, allItems }) => {
  const redirectTo = useRedirect();
  const dataProvider = useDataProvider();

  return (
    <TopToolbar>
      <div style={{ display: "flex", gap: 8 }}>
        <ButtonWithConfirm //
          label="RESET ALL"
          confirmMessage="Are you sure to reset the episode ordering?"
          onConfirm={async () => {
            await updateEpisodeOrder(dataProvider, true, allItems);

            window.location.reload();
          }}
          onCancel={() => {}}
        />
        <ButtonWithConfirm //
          label="SAVE"
          startIcon={<SaveIcon />}
          confirmMessage="Are you sure to save the episode ordering?"
          onConfirm={async () => {
            await updateEpisodeOrder(dataProvider, false, allItems);

            window.location.reload();
          }}
          onCancel={() => {}}
        />
        <ButtonWithConfirm //
          label="SAVE AND EXIT"
          startIcon={<SaveIcon />}
          confirmMessage="Are you sure to save the episode ordering and exit?"
          onConfirm={async () => {
            await updateEpisodeOrder(dataProvider, false, allItems);

            redirectTo(`/episodes?filter=${JSON.stringify({ programme_id: programmeId })}`);
          }}
          onCancel={() => {}}
        />
      </div>
    </TopToolbar>
  );
};

const DragHandle = SortableHandle(() => (
  <TableCell>
    <ListItemIcon>
      <DragHandleIcon />
    </ListItemIcon>
  </TableCell>
));

const MyDatagridRow = ({ allItems, setAllItems, item }) => {
  const notify = useNotify();

  return (
    <RecordContextProvider value={item}>
      <TableRow>
        <TableCell>
          <DragHandle />
        </TableCell>
        <TableCell style={{ overflowWrap: "anywhere" }}>{item.episode_id}</TableCell>
        <TableCell style={{ overflowWrap: "break-word" }}>{item.episode_no}</TableCell>
        <TableCell style={{ overflowWrap: "break-word" }}>{item.name_tc}</TableCell>
        <TableCell style={{ overflowWrap: "break-word" }}>{item.name_en}</TableCell>
        <TableCell style={{ overflowWrap: "break-word" }}>
          <TextField source="is_preview" />
        </TableCell>
        <TableCell style={{ overflowWrap: "break-word" }}>
          <BooleanField source="enabled" />
        </TableCell>
        <TableCell>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input type="text" name={`input_${item.episode_no}`} id={`input_${item.episode_no}`} size="small" />
            <Button
              label="INSERT"
              variant="contained"
              color="primary"
              onClick={() => {
                const inputElement = document.getElementById(`input_${item.episode_no}`);
                const inputValue = inputElement.value;

                const mineIndex = allItems.findIndex((i) => i.episode_no == item.episode_no);
                const targetIndex = allItems.findIndex((i) => i.episode_no == inputValue);

                if (targetIndex == -1) {
                  notify("Invalid episode no", "error");
                  return;
                }

                if (mineIndex == targetIndex) {
                  notify("Same episode no", "error");
                  return;
                }

                var newItems = [...allItems]; // Clone
                var oldItem = newItems[mineIndex];

                newItems.splice(mineIndex, 1); // Remove

                let insertIndex = targetIndex - 1;
                if (mineIndex < targetIndex) {
                  insertIndex = targetIndex - 1; // Target shifts down after removal
                } else {
                  insertIndex = targetIndex;
                }

                newItems.splice(insertIndex, 0, oldItem); // Insert after target

                setAllItems(newItems);

                inputElement.value = "";
              }}
            />
          </div>
        </TableCell>
      </TableRow>
    </RecordContextProvider>
  );
};

const SortableItem2 = SortableElement(({ allItems, setAllItems, item, pos, ...props }) => {
  return <MyDatagridRow allItems={allItems} setAllItems={setAllItems} item={item} pos={pos} {...props} />;
});

const MyDatagridBody = ({ pageItems, allItems, setAllItems, ...props }) => {
  return pageItems.map((item, index) => {
    if (!item) return null;
    return (
      <SortableItem2 //
        key={item.id}
        allItems={allItems}
        setAllItems={setAllItems}
        item={item}
        index={index}
        pos={index}
        {...props}
      />
    );
  });
};

const DatagridHeader = () => (
  <TableHead>
    <TableRow>
      <TableCell style={{ width: "50px" }}></TableCell> {/* Drag */}
      <TableCell style={{}}>Episode ID</TableCell>
      <TableCell style={{}}>Episode no</TableCell>
      <TableCell style={{}}>Name in TC</TableCell>
      <TableCell style={{}}>Name in EN</TableCell>
      <TableCell style={{}}>Is Preview</TableCell>
      <TableCell style={{}}>Enabled</TableCell>
      <TableCell style={{}}>Insert after episode #</TableCell> {/* Show */}
    </TableRow>
  </TableHead>
);

const MyDatagrid = SortableContainer(({ allItems, setAllItems, ...props }) => {
  const { data } = useListContext();

  const pageItems = Object.values(data);

  return (
    <Table>
      <DatagridHeader />
      <TableBody>
        <MyDatagridBody pageItems={pageItems} allItems={allItems} setAllItems={setAllItems} {...props} />
      </TableBody>
    </Table>
  );
});

const EpisodeOrderingContainer = ({ allItems, setAllItems, setProgrammeId, programmeId, ...props }) => {
  const listContext = useList({
    data: allItems,
    loading: false,
    loaded: true,
    ids: allItems.map((item) => item.id),
    resource: "episode-ordering",
    perPage: 100,
    filter: {
      programme_id: programmeId,
    },
  });

  const onSortEndWithOffset = ({ oldIndex, newIndex }) => {
    const offset = (listContext.page - 1) * listContext.perPage;
    const oldIndexWithOffset = offset + oldIndex;
    const newIndexWithOffset = offset + newIndex;
    setAllItems((items) => arrayMove(items, oldIndexWithOffset, newIndexWithOffset));
  };

  return (
    <ListContextProvider value={listContext}>
      <ListToolbar //
        filters={
          <EpisodeFilter //
            setFilters={listContext.setFilters}
            setProgrammeId={setProgrammeId}
          />
        }
        actions={<ListActions programmeId={programmeId} allItems={allItems} />}
      />

      <MyDatagrid //
        onSortEnd={onSortEndWithOffset}
        useDragHandle={true}
        lockAxis="y"
        allItems={allItems}
        setAllItems={setAllItems}
        {...props}
      />
      <Pagination rowsPerPageOptions={[100]} />
    </ListContextProvider>
  );
};

export const EpisodeOrderingList = (props) => {
  const dataProvider = useDataProvider();
  const [allItems, setAllItems] = useState([]);

  const [params] = useListParams({
    resource: "episode-ordering",
    syncWithLocation: true,
  });

  const [programmeId, setProgrammeId] = useState(params.filter?.programme_id);

  //Fetch all episodes ordering when programmeId changes
  useEffect(async () => {
    if (!programmeId) {
      setAllItems([]);
      return;
    }

    const { data } = await dataProvider.getListEpisodeOrdering({
      filter: {
        programme_id: programmeId,
      },
    });

    setAllItems(data);
  }, [programmeId]);

  return (
    <EpisodeOrderingContainer //
      {...props}
      allItems={allItems}
      setAllItems={setAllItems}
      programmeId={programmeId}
      setProgrammeId={setProgrammeId}
    />
  );
};
